/* ==========================================================================
   JMM — Qui sommes-nous ? · Moteur d'animations
   - Hero : vidéo scrubée par le scroll (interpolation lissée, seek all-intra)
   - Reveals : IntersectionObserver + stagger
   - Compteurs animés
   Compatible éditeur Shopify (shopify:section:load / unload)
   ========================================================================== */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------------
     HERO — scroll-driven video
     ---------------------------------------------------------------------- */
  function JMMHero(root) {
    this.root = root;
    this.track = root.querySelector('.jmm-hero__track');
    this.sticky = root.querySelector('.jmm-hero__sticky');
    this.video = root.querySelector('.jmm-hero__video');
    this.hint = root.querySelector('.jmm-hero__hint');
    this.sequences = Array.prototype.slice.call(root.querySelectorAll('[data-jmm-seq]'));
    this.finals = Array.prototype.slice.call(root.querySelectorAll('[data-jmm-final]'));
    this.finalStart = parseFloat(root.dataset.finalStart || '0.72');

    this.progress = 0;
    this.current = 0;
    this.target = 0;
    this.duration = 0;
    this.raf = null;
    this.destroyed = false;
    this.videoReady = false;

    this.loop = this.loop.bind(this);

    this.initVideo();
    this.computeProgress();
    this.render(true);
    // Boucle permanente : pas de dépendance à IntersectionObserver
    // (défaillant dans certaines WebViews) ; rAF se met en pause tout seul
    // quand l'onglet est masqué, et on saute le travail hors viewport.
    this.raf = requestAnimationFrame(this.loop);
  }

  JMMHero.prototype.initVideo = function () {
    var v = this.video;
    if (!v) return;
    var self = this;

    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var src = isMobile && v.dataset.srcMobile ? v.dataset.srcMobile : v.dataset.srcDesktop;

    // Le scrub suit le geste de scroll de l'utilisateur : il reste actif même
    // avec prefers-reduced-motion. Le poster ne sert que si la vidéo ne charge pas.
    if (!src) {
      this.root.classList.add('jmm-hero--fallback');
      return;
    }

    v.muted = true;
    v.setAttribute('muted', '');
    v.playsInline = true;
    v.preload = 'auto';
    v.src = src;

    var markReady = function () {
      if (self.videoReady) return;
      if (!v.duration || isNaN(v.duration)) return;
      self.videoReady = true;
      self.duration = v.duration;
      // Amorce le décodage (iOS) puis fige : la timeline appartient au scroll.
      var p = v.play();
      if (p && p.then) {
        p.then(function () { v.pause(); }).catch(function () {});
      } else {
        v.pause();
      }
      self.render(true);
    };

    v.addEventListener('loadedmetadata', markReady);
    v.addEventListener('canplay', markReady);
    v.load();

    // Sécurité : si la vidéo ne se charge pas, on bascule sur le poster.
    setTimeout(function () {
      if (!self.videoReady) self.root.classList.add('jmm-hero--fallback');
    }, 6000);
    v.addEventListener('canplay', function () {
      self.root.classList.remove('jmm-hero--fallback');
    });
  };

  JMMHero.prototype.computeProgress = function () {
    var rect = this.track.getBoundingClientRect();
    var vh = window.innerHeight;
    var total = rect.height - vh;
    if (total <= 0) { this.progress = 1; return rect; }
    var p = -rect.top / total;
    this.progress = Math.max(0, Math.min(1, p));
    return rect;
  };

  JMMHero.prototype.loop = function () {
    if (this.destroyed) return;
    this.raf = requestAnimationFrame(this.loop);
    var rect = this.computeProgress();
    // hero loin hors écran et interpolation terminée : rien à faire
    var vh = window.innerHeight;
    if ((rect.bottom < -vh || rect.top > vh * 2) && Math.abs(this.progress - this.current) < 0.0005) return;
    this.render(false);
  };

  JMMHero.prototype.render = function (immediate) {
    var p = this.progress;

    // Lissage : la valeur affichée court après la valeur cible.
    this.target = p;
    var ease = immediate ? 1 : 0.14;
    this.current += (this.target - this.current) * ease;
    if (Math.abs(this.target - this.current) < 0.0004) this.current = this.target;
    var c = this.current;

    this.root.style.setProperty('--jmm-p', c.toFixed(4));

    // Scrub vidéo — fastSeek quand disponible (instantané : chaque frame est
    // une image clé) ; un seek en attente ne bloque pas un rattrapage important.
    if (this.videoReady && this.duration) {
      var t = c * (this.duration - 0.04);
      var delta = Math.abs(this.video.currentTime - t);
      if (delta > 0.008 && (!this.video.seeking || delta > 0.15)) {
        try {
          if (this.video.fastSeek) this.video.fastSeek(t);
          else this.video.currentTime = t;
        } catch (e) { /* noop */ }
      }
    }

    // Séquences narratives
    for (var i = 0; i < this.sequences.length; i++) {
      var el = this.sequences[i];
      var s = parseFloat(el.dataset.start || '0');
      var e = parseFloat(el.dataset.end || '0.3');
      var fade = Math.min(0.35 * (e - s), 0.09);
      var o = 0;
      if (c > s && c < e) {
        var inO = Math.min(1, (c - s) / fade);
        var outO = Math.min(1, (e - c) / fade);
        o = Math.min(inO, outO);
      }
      el.style.opacity = o.toFixed(3);
      var drift = (1 - o) * 2.4;
      el.style.transform = 'translateY(' + (c < (s + e) / 2 ? drift : -drift) + 'rem)';
    }

    // Composition finale (l'image de référence) — révélation en cascade,
    // réversible puisque pilotée par la position de scroll.
    var fs = this.finalStart;
    for (var j = 0; j < this.finals.length; j++) {
      var fel = this.finals[j];
      var start = fs + j * 0.035;
      var span = 0.09;
      var fo = Math.max(0, Math.min(1, (c - start) / span));
      // easing doux
      fo = 1 - Math.pow(1 - fo, 3);
      fel.style.opacity = fo.toFixed(3);
      fel.style.transform = 'translateY(' + ((1 - fo) * 2.6).toFixed(2) + 'rem)';
    }

    if (this.hint) {
      this.hint.style.opacity = c > 0.03 ? '0' : '1';
    }
  };

  JMMHero.prototype.destroy = function () {
    this.destroyed = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
  };

  /* ----------------------------------------------------------------------
     Reveals génériques — via la boucle globale (pas d'IntersectionObserver,
     défaillant dans certaines WebViews mobiles)
     ---------------------------------------------------------------------- */
  var revealPending = [];
  var countPending = [];

  function initReveals(scope) {
    var els = (scope || document).querySelectorAll('[data-jmm-reveal]');
    if (!els.length) return;
    Array.prototype.forEach.call(els, function (el) {
      if (el.dataset.jmmRevealDone) return;
      el.dataset.jmmRevealDone = '1';
      if (reducedMotion) {
        el.classList.add('is-inview');
      } else {
        revealPending.push(el);
      }
    });
  }

  /* ----------------------------------------------------------------------
     Compteurs
     ---------------------------------------------------------------------- */
  function animateCount(el) {
    var end = parseFloat(el.dataset.jmmCount || '0');
    if (reducedMotion || !end) {
      el.textContent = el.dataset.jmmCount || '0';
      return;
    }
    var dur = 1800;
    var t0 = null;
    function step(ts) {
      if (!t0) t0 = ts;
      var k = Math.min(1, (ts - t0) / dur);
      k = 1 - Math.pow(1 - k, 4);
      el.textContent = Math.round(end * k).toString();
      if (k < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initCounters(scope) {
    var els = (scope || document).querySelectorAll('[data-jmm-count]');
    Array.prototype.forEach.call(els, function (el) {
      if (el.dataset.jmmCountDone) return;
      el.dataset.jmmCountDone = '1';
      countPending.push(el);
    });
  }

  /* marquage des éléments entrés dans le viewport (throttlé dans la boucle) */
  function checkPending() {
    var vh = window.innerHeight;
    var limit = vh * 0.94;
    for (var i = revealPending.length - 1; i >= 0; i--) {
      var el = revealPending[i];
      if (!el.isConnected) { revealPending.splice(i, 1); continue; }
      var r = el.getBoundingClientRect();
      if (r.top < limit && r.bottom > 0) {
        el.classList.add('is-inview');
        revealPending.splice(i, 1);
      }
    }
    for (var j = countPending.length - 1; j >= 0; j--) {
      var c = countPending[j];
      if (!c.isConnected) { countPending.splice(j, 1); continue; }
      var cr = c.getBoundingClientRect();
      if (cr.top < vh * 0.85 && cr.bottom > 0) {
        animateCount(c);
        countPending.splice(j, 1);
      }
    }
  }

  /* ----------------------------------------------------------------------
     Split-text : chaque mot des grands titres monte depuis un masque
     ---------------------------------------------------------------------- */
  function splitWords(el) {
    if (el.dataset.jmmSplitDone) return;
    el.dataset.jmmSplitDone = '1';
    var idx = 0;
    (function process(node) {
      var children = Array.prototype.slice.call(node.childNodes);
      children.forEach(function (child) {
        if (child.nodeType === 3) {
          var frag = document.createDocumentFragment();
          var parts = child.textContent.split(/(\s+)/);
          parts.forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(' '));
              return;
            }
            var w = document.createElement('span');
            w.className = 'jmm-w';
            var inner = document.createElement('span');
            inner.className = 'jmm-wi';
            inner.textContent = part;
            inner.style.transitionDelay = (0.1 + idx * 0.055).toFixed(3) + 's';
            w.appendChild(inner);
            frag.appendChild(w);
            idx++;
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1 && child.className.indexOf('jmm-w') === -1) {
          process(child);
        }
      });
    })(el);
  }

  function initSplits(scope) {
    if (reducedMotion) return;
    var els = (scope || document).querySelectorAll(
      '.jmm-story__title, .jmm-pillars__title, .jmm-agences__title, .jmm-cta__title'
    );
    Array.prototype.forEach.call(els, splitWords);
  }

  /* ----------------------------------------------------------------------
     Boucle globale : parallaxe + marquee piloté par la vitesse de scroll
     ---------------------------------------------------------------------- */
  var pxItems = [];
  var marquees = [];
  var lastScrollY = window.scrollY || 0;
  var scrollVel = 0;
  var globalRaf = null;
  var frameCount = 0;

  function globalLoop() {
    globalRaf = requestAnimationFrame(globalLoop);
    var y = window.scrollY || 0;
    var v = y - lastScrollY;
    lastScrollY = y;
    scrollVel += (v - scrollVel) * 0.12;
    var vh = window.innerHeight;

    frameCount++;
    if (frameCount % 8 === 0 || frameCount === 1) checkPending();

    for (var i = 0; i < pxItems.length; i++) {
      var it = pxItems[i];
      if (!it.el.isConnected) continue;
      var r = it.el.getBoundingClientRect();
      if (r.bottom < -250 || r.top > vh + 250) continue;
      var off = (r.top + r.height / 2 - vh / 2) * it.speed;
      it.el.style.transform = (it.base ? it.base + ' ' : '') + 'translate3d(0,' + off.toFixed(1) + 'px,0)';
    }

    for (var j = 0; j < marquees.length; j++) {
      var m = marquees[j];
      if (!m.track.isConnected) continue;
      if (!m.half) m.half = m.track.scrollWidth / 2;
      if (!m.half) continue;
      var target = m.base + Math.max(-80, Math.min(80, scrollVel)) * 0.55;
      m.speed += (target - m.speed) * 0.06;
      m.x -= m.speed;
      if (m.x <= -m.half) m.x += m.half;
      if (m.x > 0) m.x -= m.half;
      m.track.style.transform = 'translate3d(' + m.x.toFixed(1) + 'px,0,0)';
    }
  }

  function ensureGlobalLoop() {
    if (!globalRaf && (pxItems.length || marquees.length || revealPending.length || countPending.length)) {
      globalRaf = requestAnimationFrame(globalLoop);
    }
  }

  function initParallax(scope) {
    if (reducedMotion) return;
    var els = (scope || document).querySelectorAll('[data-jmm-parallax]');
    Array.prototype.forEach.call(els, function (el) {
      if (el.dataset.jmmPxDone) return;
      el.dataset.jmmPxDone = '1';
      pxItems.push({
        el: el,
        speed: parseFloat(el.dataset.jmmParallax || '0.1'),
        base: el.dataset.jmmParallaxBase || ''
      });
    });
  }

  function initMarquees(scope) {
    if (reducedMotion) return;
    var tracks = (scope || document).querySelectorAll('.jmm-marquee__track');
    Array.prototype.forEach.call(tracks, function (track) {
      if (track.dataset.jmmMqDone) return;
      track.dataset.jmmMqDone = '1';
      var wrap = track.closest('.jmm-marquee');
      if (wrap) wrap.classList.add('jmm-marquee--js');
      marquees.push({ track: track, x: 0, speed: 1.1, base: 1.1, half: 0 });
    });
  }

  window.addEventListener('resize', function () {
    for (var i = 0; i < marquees.length; i++) marquees[i].half = 0;
  });

  /* ----------------------------------------------------------------------
     Boutons magnétiques + tilt 3D des cartes agences (pointeurs précis)
     ---------------------------------------------------------------------- */
  var finePointer = window.matchMedia('(pointer: fine)').matches;

  function initMagnetic(scope) {
    if (reducedMotion || !finePointer) return;
    var btns = (scope || document).querySelectorAll('.jmm-btn');
    Array.prototype.forEach.call(btns, function (btn) {
      if (btn.dataset.jmmMag) return;
      btn.dataset.jmmMag = '1';
      btn.addEventListener('mousemove', function (e) {
        var r = btn.getBoundingClientRect();
        var dx = (e.clientX - r.left - r.width / 2) * 0.22;
        var dy = (e.clientY - r.top - r.height / 2) * 0.38;
        dx = Math.max(-10, Math.min(10, dx));
        dy = Math.max(-8, Math.min(8, dy));
        btn.style.transform = 'translate(' + dx.toFixed(1) + 'px,' + dy.toFixed(1) + 'px)';
      });
      btn.addEventListener('mouseleave', function () {
        btn.style.transform = '';
      });
    });
  }

  function initTilt(scope) {
    if (reducedMotion || !finePointer) return;
    var cards = (scope || document).querySelectorAll('.jmm-agence');
    Array.prototype.forEach.call(cards, function (card) {
      if (card.dataset.jmmTilt) return;
      card.dataset.jmmTilt = '1';
      card.addEventListener('mousemove', function (e) {
        var r = card.getBoundingClientRect();
        var rx = -((e.clientY - r.top) / r.height - 0.5) * 5;
        var ry = ((e.clientX - r.left) / r.width - 0.5) * 6;
        card.classList.add('is-tilting');
        card.style.transform =
          'perspective(90rem) rotateX(' + rx.toFixed(2) + 'deg) rotateY(' + ry.toFixed(2) + 'deg) translateY(-0.8rem)';
      });
      card.addEventListener('mouseleave', function () {
        card.classList.remove('is-tilting');
        card.style.transform = '';
      });
    });
  }

  /* ----------------------------------------------------------------------
     Orchestration + éditeur Shopify
     ---------------------------------------------------------------------- */
  var heroes = [];

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-jmm-hero]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__jmmHero) return;
      root.__jmmHero = new JMMHero(root);
      heroes.push(root.__jmmHero);
    });
    initSplits(scope);
    initReveals(scope);
    initCounters(scope);
    initParallax(scope);
    initMarquees(scope);
    initMagnetic(scope);
    initTilt(scope);
    ensureGlobalLoop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (evt) {
    initAll(evt.target);
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var root = evt.target.querySelector('[data-jmm-hero]');
    if (root && root.__jmmHero) {
      root.__jmmHero.destroy();
      root.__jmmHero = null;
    }
  });
})();
